/**
 * Product-card image swipe gallery.
 *
 * Activates on any .product-card__media that contains .eros-slide images.
 * Handles pointer events (mouse drag + touch) to cycle through images in a
 * full loop — swiping past the last image wraps to the first, and swiping
 * before the first wraps to the last. Dot indicators are appended INSIDE
 * .product-card__media so they position correctly at the bottom of the
 * image area.
 *
 * Lazy loading: slide <img> tags render from Liquid with no src/srcset (see
 * product-card.liquid) — only data-eros-srcset/data-eros-src — so nothing
 * is fetched at page load. ensureLoaded() copies the real URLs into
 * src/srcset the first time the shopper shows intent to use the gallery
 * (pointerenter, pointerdown, or touchstart — whichever fires first, once
 * per card).
 *
 * One-time hint: the first gallery card that becomes VISIBLE (50% in
 * viewport, via IntersectionObserver — never a zero-size card hidden in a
 * drawer/menu) plays a slow CSS-driven half-slide reveal (see
 * eros-commerce.css's .eros-hinting / @keyframes eros-gallery-hint-*) to
 * signal swipeability, then settles back — purely visual, it never touches
 * `current` or the dots.
 *
 * Key fixes vs v1:
 *  - Dots appended inside <a> (not figure) — correct positioning parent.
 *  - touch-action: pan-y CSS lets the browser scroll vertically while we
 *    own horizontal drag.
 *  - moved flag preserved through pointerup so click handler can prevent
 *    navigation after a real swipe.
 *  - One-time hint: first card with multiple images briefly slides the
 *    primary/secondary halfway then returns, signalling swipeability —
 *    waits for that slide to actually decode before animating so the hint
 *    never flashes blank, and bails out silently if the shopper has
 *    already started interacting with the card.
 */
(function () {
  'use strict';

  var DRAG_THRESHOLD = 22; // px minimum horizontal drag to register a swipe

  // ── Hint arming ─────────────────────────────────────────────────────────
  // The hint goes to the FIRST gallery card the shopper actually SEES — not
  // the first one in DOM order (which can be inside a hidden drawer/menu and
  // has a zero-size box, so the animation would play invisibly). Every
  // multi-image card registers here; a shared IntersectionObserver awards
  // the hint to the first card reaching 50% visibility, then disconnects.
  var hintClaimed = false;
  var hintObserver = null;

  function armHint(media, playFn) {
    if (hintClaimed) return;
    if (!('IntersectionObserver' in window)) {
      // Ancient-browser fallback: first registered card, fixed delay.
      hintClaimed = true;
      setTimeout(playFn, 900);
      return;
    }
    if (!hintObserver) {
      hintObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (hintClaimed || !entry.isIntersecting || entry.intersectionRatio < 0.5) return;
          hintClaimed = true;
          hintObserver.disconnect();
          var fn = entry.target._erosHintPlay;
          if (fn) setTimeout(fn, 400);
        });
      }, { threshold: 0.5 });
    }
    media._erosHintPlay = playFn;
    hintObserver.observe(media);
  }

  // ── Lazy materialization ────────────────────────────────────────────────
  // Slide <img>s carry data-eros-srcset/data-eros-src instead of real
  // src/srcset so the browser fetches nothing at page load. Copying the
  // data attributes into src/srcset kicks off the real fetch; removing the
  // data attributes afterward makes repeat calls a no-op (idempotent).
  function ensureLoaded(media, slides) {
    slides.forEach(function (img) {
      if (img.dataset.erosSrcset === undefined) return; // already materialized
      img.srcset = img.dataset.erosSrcset;
      img.src = img.dataset.erosSrc;
      delete img.dataset.erosSrcset;
      delete img.dataset.erosSrc;
      img.decode().catch(function () {});
    });
  }

  // ── Ready check used only by the hint, so it never animates a blank slide ──
  // Prefers decode() (resolves once the image is actually paintable);
  // falls back to the load event for browsers without decode(), racing a
  // 300ms timeout in case the image is already complete and 'load' won't
  // fire again.
  function whenSlideReady(img, done) {
    if (typeof img.decode === 'function') {
      img.decode().then(done, function () {}); // offline/broken — skip hint silently
      return;
    }
    if (img.complete) {
      var fired = false;
      var fire = function () {
        if (fired) return;
        fired = true;
        done();
      };
      img.addEventListener('load', fire, { once: true });
      setTimeout(fire, 300);
    } else {
      img.addEventListener('load', done, { once: true });
    }
  }

  function initCard(media) {
    var slides = Array.prototype.slice.call(media.querySelectorAll('.eros-slide'));
    var n = slides.length;
    if (n < 1) return;

    var total = n + 1; // 0 = primary; 1..n = slide[0..n-1]
    var current = 0;

    // ── Dot strip — appended INSIDE the <a> media element ───────────────────
    // The <a> already has position:relative + overflow:hidden from theme.css,
    // so position:absolute dots land at the bottom of the image area.
    var dots = document.createElement('div');
    dots.className = 'eros-gallery-dots';
    for (var i = 0; i < total; i++) {
      var dot = document.createElement('span');
      dot.className = 'eros-gallery-dot' + (i === 0 ? ' is-active' : '');
      dots.appendChild(dot);
    }
    media.appendChild(dots);

    // ── goTo — full loop; callers wrap the index with modulo ────────────────
    function goTo(idx) {
      if (idx === current) return;
      if (current > 0) slides[current - 1].classList.remove('is-active');
      dots.children[current].classList.remove('is-active');
      current = idx;
      if (current === 0) {
        media.classList.remove('eros-gallery-on');
      } else {
        media.classList.add('eros-gallery-on');
        slides[current - 1].classList.add('is-active');
      }
      dots.children[current].classList.add('is-active');
    }

    // ── Lazy materialization on first user intent ────────────────────────────
    // pointerenter fires before theme.css's `pointer: fine` hover-swap needs
    // the secondary image, so the fetch is already underway by the time the
    // CSS hover transition needs pixels. pointerdown/touchstart cover touch,
    // where there's no hover — whichever fires first wins; the materialized
    // flag guards against the other two firing right after.
    var materialized = false;
    function materializeOnce() {
      if (materialized) return;
      materialized = true;
      ensureLoaded(media, slides);
    }
    media.addEventListener('pointerenter', materializeOnce, { passive: true, once: true });
    media.addEventListener('pointerdown', materializeOnce, { passive: true, once: true });
    media.addEventListener('touchstart', materializeOnce, { passive: true, once: true });

    // ── Swipe / drag ─────────────────────────────────────────────────────────
    var startX = null;
    var moved = false;
    var captureId = null;
    var userInteracted = false; // set on first real pointerdown — suppresses the hint if it hasn't fired yet

    media.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      userInteracted = true;
      startX = e.clientX;
      moved = false;
      captureId = e.pointerId;
      try { media.setPointerCapture(e.pointerId); } catch (_) {}
    });

    media.addEventListener('pointermove', function (e) {
      if (startX === null || e.pointerId !== captureId) return;
      if (Math.abs(e.clientX - startX) > 5) moved = true;
    }, { passive: true });

    media.addEventListener('pointerup', function (e) {
      if (startX === null || e.pointerId !== captureId) return;
      var dx = e.clientX - startX;
      startX = null;
      captureId = null;
      if (!moved || Math.abs(dx) < DRAG_THRESHOLD) { moved = false; return; }
      // Leave moved=true so the click handler can suppress navigation.
      if (dx < 0) goTo((current + 1) % total);
      else goTo((current - 1 + total) % total);
    });

    media.addEventListener('pointercancel', function () {
      startX = null;
      captureId = null;
      moved = false;
    }, { passive: true });

    // Prevent <a> navigation when the gesture was a drag, not a tap.
    media.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); moved = false; }
    });

    // ── One-time swipe hint (first VISIBLE gallery card only) ───────────────
    // Purely visual (slow half-slide reveal via CSS animation — see
    // .eros-hinting / @keyframes eros-gallery-hint-* in eros-commerce.css).
    // Does not call goTo, so `current` and the dots are untouched. Arming is
    // visibility-gated — see armHint() above.
    if (total > 1) {
      armHint(media, function () {
        ensureLoaded(media, slides);
        whenSlideReady(slides[0], function () {
          if (userInteracted) return; // shopper already engaged — skip the hint
          media.addEventListener('animationend', function () {
            media.classList.remove('eros-hinting');
          }, { once: true });
          media.classList.add('eros-hinting');
        });
      });
    }
  }

  // ── Init pass ──────────────────────────────────────────────────────────────
  function run(root) {
    var scope = root || document;
    scope.querySelectorAll('.product-card__media').forEach(function (media) {
      if (media.dataset.erosGalleryInit) return;
      media.dataset.erosGalleryInit = '1';
      initCard(media);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { run(); });
  } else {
    run();
  }

  // Re-run on dynamically injected cards (infinite scroll, pagination).
  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.classList && node.classList.contains('product-card__media')) {
            if (!node.dataset.erosGalleryInit) { node.dataset.erosGalleryInit = '1'; initCard(node); }
          } else {
            run(node);
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
